import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useStrings } from '../i18n';
import PanchTatvaButton from '../components/PanchTatvaButton';
import { initiatePayment } from '../services/paymentService';
import { getUserProfile } from '../services/userService';
import { getShippingOptions, NO_COVERAGE } from '../services/shipmentService';

// ── Static fallback shipping options ────────────────────────────────────────
// Shown when Shiprocket API is unavailable (503, timeout, network error).
// v2.7 field names: price (not cost), etdDays (not estimatedDays).
// Backend spec: docs/backend/CHECKOUT_HARDENING_BACKEND.md
const STATIC_SHIPPING_OPTIONS = [
  {
    id: 'standard',
    name: 'Standard Delivery',
    price: 0,
    etdDays: 5,
    etd: '3–5 Days',
    rating: null,
    codAvailable: false,
    isAir: false,
    isRecommended: true,
  },
  {
    id: 'express',
    name: 'Express Delivery',
    price: 100,
    etdDays: 2,
    etd: '1–2 Days',
    rating: null,
    codAvailable: false,
    isAir: true,
    isRecommended: false,
  },
];

// Debounce helper — avoids hammering the Shiprocket API on every keystroke
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debouncedValue;
}

export default function Checkout() {
  const navigate = useNavigate();
  const { userInfo, getIdToken, isAuthenticated } = useAuth();
  const { cart, clearCart } = useCart();
  const strings = useStrings();
  const co = strings.checkout || {};

  // ── Steps ──────────────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(1);

  // ── Address state ──────────────────────────────────────────────────────────
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [addressesError, setAddressesError] = useState('');
  // null = no saved address selected (showing form), string = _id of selected address
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  // 'list' = showing address picker  |  'form' = showing manual entry form
  const [addressMode, setAddressMode] = useState('form');

  // ── Form data ──────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    fullName: userInfo?.displayName || '',
    phone: '',
    email: userInfo?.email || '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    saveAsDefault: false,
  });
  const [errors, setErrors] = useState({});

  // ── Shipping options state ─────────────────────────────────────────────────
  // null = not yet fetched  |  [...] = options available
  const [shippingOptions, setShippingOptions] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  // '' | 'fallback' | 'rate_limit' | 'no_coverage' | 'unavailable'
  const [shippingError, setShippingError] = useState('');
  const [isFallback, setIsFallback] = useState(false);
  const [selectedShippingOption, setSelectedShippingOption] = useState(null);

  // Debounce pincode so we don't call Shiprocket on every character
  const debouncedPincode = useDebounce(formData.pincode, 400);

  // Track last fetched pincode to avoid redundant calls
  const lastFetchedPincodeRef = useRef('');
  const lastFetchedCartCountRef = useRef(0);

  // ── Totals (recomputed when shipping selection changes) ────────────────────
  const shippingCost = selectedShippingOption?.price ?? 0;
  const subtotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const tax = Math.round((subtotal + shippingCost) * 0.18);
  const total = subtotal + shippingCost + tax;

  // ── Fetch saved addresses on mount (authenticated users only) ─────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const load = async () => {
      setAddressesLoading(true);
      setAddressesError('');
      try {
        const idToken = await getIdToken();
        const profile = await getUserProfile(null, idToken);
        if (cancelled) return;
        const addrs = profile?.addresses || [];
        setSavedAddresses(addrs);
        if (addrs.length > 0) {
          setAddressMode('list');
          const defaultAddr = addrs.find((a) => a.isDefault) || addrs[0];
          setSelectedAddressId(defaultAddr._id);
          prefillFromAddress(defaultAddr);
        }
      } catch {
        if (cancelled) return;
        setAddressesError(co.addressLoadError || 'Could not load saved addresses. Please enter manually.');
        setAddressMode('form');
      } finally {
        if (!cancelled) setAddressesLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // ── Pre-fill form from a saved address ────────────────────────────────────
  // Address schema (from EditProfile): { name, phone, address, city, state, pincode, label, isDefault, id }
  const prefillFromAddress = useCallback((addr) => {
    setFormData((prev) => ({
      ...prev,
      fullName: addr.name || addr.fullName || prev.fullName,
      phone: addr.phone || prev.phone,
      address: addr.address || addr.line1 || prev.address,
      city: addr.city || prev.city,
      state: addr.state || prev.state,
      pincode: addr.pincode || prev.pincode,
    }));
    setErrors({});
  }, []);

  // ── Auto-select cheapest (recommended) or first option ────────────────────
  const autoSelectCheapest = useCallback((options) => {
    if (!options || options.length === 0) return;
    const recommended = options.find((o) => o.isRecommended) || options[0];
    setSelectedShippingOption(recommended);
  }, []);

  // ── Fetch shipping options ─────────────────────────────────────────────────
  const fetchShippingOptions = useCallback(async (pincode) => {
    if (!pincode || !/^\d{6}$/.test(pincode)) return;
    if (
      pincode === lastFetchedPincodeRef.current &&
      cart.length === lastFetchedCartCountRef.current
    ) return;

    lastFetchedPincodeRef.current = pincode;
    lastFetchedCartCountRef.current = cart.length;

    setShippingLoading(true);
    setShippingError('');
    setIsFallback(false);

    try {
      const result = await getShippingOptions(pincode, cart, subtotal);
      // NO_COVERAGE = 422: pincode has no supported courier coverage
      if (result === NO_COVERAGE) {
        setShippingOptions([]);
        setShippingError('no_coverage');
        setSelectedShippingOption(null);
      } else if (result === null) {
        // null = 503 / 500 / timeout — silently fall back to static
        setShippingOptions(STATIC_SHIPPING_OPTIONS);
        setIsFallback(true);
        setShippingError('unavailable');
        autoSelectCheapest(STATIC_SHIPPING_OPTIONS);
      } else {
        // Success — real courier array (1–3 items, never empty on 200)
        setShippingOptions(result);
        setIsFallback(false);
        setShippingError('');
        autoSelectCheapest(result);
      }
    } catch (err) {
      if (err.status === 429) {
        setShippingOptions(STATIC_SHIPPING_OPTIONS);
        setIsFallback(true);
        setShippingError('rate_limit');
        autoSelectCheapest(STATIC_SHIPPING_OPTIONS);
      } else {
        setShippingOptions(STATIC_SHIPPING_OPTIONS);
        setIsFallback(true);
        setShippingError('unavailable');
        autoSelectCheapest(STATIC_SHIPPING_OPTIONS);
      }
    } finally {
      setShippingLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, subtotal, autoSelectCheapest]);

  // ── Trigger shipping fetch when debounced pincode changes ─────────────────
  useEffect(() => {
    if (debouncedPincode && /^\d{6}$/.test(debouncedPincode)) {
      fetchShippingOptions(debouncedPincode);
    } else if (debouncedPincode && debouncedPincode.length > 0) {
      setShippingOptions(null);
      setSelectedShippingOption(null);
    }
  }, [debouncedPincode, fetchShippingOptions]);

  // ── Re-fetch when cart changes (items added/removed during checkout) ───────
  useEffect(() => {
    if (debouncedPincode && /^\d{6}$/.test(debouncedPincode)) {
      lastFetchedPincodeRef.current = '';
      fetchShippingOptions(debouncedPincode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length]);

  // ── Input handlers ─────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'pincode') {
      setShippingOptions(null);
      setSelectedShippingOption(null);
      lastFetchedPincodeRef.current = '';
    }
  };

  // ── Address picker handlers ────────────────────────────────────────────────
  const handleSelectSavedAddress = (addr) => {
    setSelectedAddressId(addr._id);
    prefillFromAddress(addr);
    setShippingOptions(null);
    setSelectedShippingOption(null);
    lastFetchedPincodeRef.current = '';
  };

  const handleUseNewAddress = () => {
    setSelectedAddressId(null);
    setFormData({
      fullName: userInfo?.displayName || '',
      phone: '',
      email: userInfo?.email || '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      saveAsDefault: false,
    });
    setErrors({});
    setAddressMode('form');
    setShippingOptions(null);
    setSelectedShippingOption(null);
    lastFetchedPincodeRef.current = '';
  };

  const handleBackToAddressList = () => {
    setAddressMode('list');
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, '')))
      newErrors.phone = 'Enter valid 10-digit phone number';
    if (!formData.email.trim()) newErrors.email = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = 'Enter a valid email address';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(formData.pincode))
      newErrors.pincode = 'Enter valid 6-digit pincode';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    if (shippingError === 'no_coverage') return false;
    return !!selectedShippingOption;
  };

  // ── Step navigation ────────────────────────────────────────────────────────
  const handleNextStep = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
      if (formData.pincode && /^\d{6}$/.test(formData.pincode)) {
        fetchShippingOptions(formData.pincode);
      }
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  // ── Payment ────────────────────────────────────────────────────────────────
  const handlePayment = async () => {
    try {
      const idToken = isAuthenticated ? await getIdToken() : null;
      const payerName = isAuthenticated
        ? userInfo?.displayName || formData.fullName
        : formData.fullName;
      const payerEmail = isAuthenticated
        ? userInfo?.email || formData.email
        : formData.email;

      const orderItems = cart.map((item) => ({
        id: String(item.id),
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        image: item.image || '',
        element: item.element || '',
      }));

      const orderPricing = { subtotal, shipping: shippingCost, tax, total };

      const orderShippingAddress = {
        fullName: formData.fullName,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        pincode: formData.pincode,
      };

      const result = await initiatePayment({
        amount: total,
        idToken,
        userInfo: isAuthenticated ? userInfo : null,
        guestName: isAuthenticated ? null : payerName,
        guestEmail: isAuthenticated ? null : payerEmail,
        prefillName: payerName,
        prefillEmail: payerEmail,
        razorpayKeyId: process.env.REACT_APP_RAZORPAY_KEY,
        items: orderItems,
        pricing: orderPricing,
        shippingAddress: orderShippingAddress,
        shippingMethod: selectedShippingOption?.id || 'standard',
      });

      clearCart();
      navigate(`/order-confirmation/${result.orderId}`, {
        state: {
          order: result.order,
          guestEmail: isAuthenticated ? null : payerEmail,
        },
      });
    } catch (error) {
      if (error.message === 'Payment cancelled by user') return;
      console.error('Payment error:', error);
      navigate('/payment-failed', { state: { error: error.message } });
    }
  };

  // ── Display options (fallback to static if not yet fetched) ───────────────
  const displayOptions = shippingOptions ?? STATIC_SHIPPING_OPTIONS;

  // ── Empty cart guard ───────────────────────────────────────────────────────
  if (cart.length === 0) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-12 md:py-20 text-center fade-section show">
        <h1 className="text-3xl md:text-4xl font-heading text-headingText mb-6">
          {co.cartEmpty || 'Your cart is empty'}
        </h1>
        <p className="text-bodyText mb-8 font-sans">
          {co.cartEmptyDesc || 'Add items before proceeding to checkout'}
        </p>
        <PanchTatvaButton onClick={() => navigate('/shop')}>
          {co.continueShopping || 'Continue Shopping'}
        </PanchTatvaButton>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto px-4 md:px-6 py-12 md:py-20 fade-section show">

      {/* Guest sign-in prompt banner */}
      {!isAuthenticated && (
        <div className="mb-6 p-4 bg-surface rounded-xl border border-secondary/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <p className="font-sans font-semibold text-bodyText text-sm">
              {co.guestSignInPrompt || 'Sign in for faster checkout'}
            </p>
            <p className="font-sans text-xs text-textSecondary mt-0.5">
              Saved addresses, order history and order tracking
            </p>
          </div>
          <Link
            to="/login"
            state={{ from: '/checkout' }}
            className="text-sm font-semibold text-secondary hover:underline whitespace-nowrap font-sans"
          >
            {strings.auth?.login || 'Sign in'}
          </Link>
        </div>
      )}

      {/* Progress Indicator */}
      <div className="mb-8 md:mb-12">
        <div className="flex items-center justify-between mb-4 relative">
          <div className="absolute left-0 right-0 top-5 h-0.5 bg-gray-300 z-0 mx-10" />
          <div
            className="absolute left-0 top-5 h-0.5 bg-secondary z-0 transition-all duration-300"
            style={{
              marginLeft: '2.5rem',
              marginRight: '2.5rem',
              width:
                currentStep === 1
                  ? '0%'
                  : currentStep === 2
                  ? 'calc(50% - 2.5rem)'
                  : 'calc(100% - 5rem)',
            }}
          />
          {[
            { step: 1, label: co.stepAddress || 'Address' },
            { step: 2, label: co.stepShipping || 'Shipping' },
            { step: 3, label: co.stepReview || 'Review' },
          ].map(({ step, label }) => (
            <div key={step} className="flex flex-col items-center z-10">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm border-2 transition-all font-sans ${
                  step <= currentStep
                    ? 'bg-secondary text-primaryBg border-secondary'
                    : 'bg-primaryBg text-gray-500 border-gray-300'
                }`}
              >
                {step < currentStep ? '✓' : step}
              </div>
              <span
                className={`mt-2 text-xs md:text-sm font-medium font-sans ${
                  step <= currentStep ? 'text-secondary' : 'text-gray-400'
                }`}
              >
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* ── Form Section ── */}
        <div className="lg:col-span-2">
          <div className="bg-cardBg rounded-2xl p-6 md:p-8 shadow-card">

            {/* ════════════════════════════════════════════
                STEP 1 — ADDRESS
            ════════════════════════════════════════════ */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-heading text-headingText">
                  {co.shippingAddress || 'Shipping Address'}
                </h2>

                {/* Loading addresses skeleton */}
                {addressesLoading && (
                  <div className="flex items-center gap-3 p-4 bg-surface rounded-xl">
                    <svg className="animate-spin h-5 w-5 text-secondary" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    <span className="text-sm text-bodyText font-sans">
                      {co.loadingAddresses || 'Loading your addresses…'}
                    </span>
                  </div>
                )}

                {/* Address load error */}
                {addressesError && !addressesLoading && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600 font-sans">
                    {addressesError}
                  </div>
                )}

                {/* Saved address picker */}
                {isAuthenticated && !addressesLoading && savedAddresses.length > 0 && addressMode === 'list' && (
                  <div className="space-y-3">
                    <p className="text-sm font-semibold text-bodyText font-sans">
                      {co.savedAddresses || 'Saved Addresses'}
                    </p>
                    {savedAddresses.map((addr) => (
                      <label
                        key={addr._id}
                        className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          selectedAddressId === addr._id
                            ? 'border-secondary bg-secondary/5'
                            : 'border-gray-200 hover:border-secondary/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="savedAddress"
                          value={addr._id}
                          checked={selectedAddressId === addr._id}
                          onChange={() => handleSelectSavedAddress(addr)}
                          className="mt-1 w-4 h-4 accent-secondary"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-bodyText text-sm font-sans">
                            {addr.name || addr.fullName}
                            {addr.isDefault && (
                              <span className="ml-2 text-xs bg-secondary/10 text-secondary px-2 py-0.5 rounded-full">
                                Default
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-textSecondary mt-0.5 font-sans">
                            {addr.address || addr.line1}, {addr.city}, {addr.state} — {addr.pincode}
                          </p>
                          {addr.phone && (
                            <p className="text-xs text-textSecondary font-sans">{addr.phone}</p>
                          )}
                        </div>
                      </label>
                    ))}

                    {/* Add New Address option */}
                    <button
                      type="button"
                      onClick={handleUseNewAddress}
                      className="w-full flex items-center gap-3 p-4 border-2 border-dashed border-gray-300 rounded-xl text-sm text-textSecondary hover:border-secondary hover:text-secondary transition-all font-sans"
                    >
                      {co.addNewAddress || '+ Add New Address'}
                    </button>
                  </div>
                )}

                {/* Manual address form */}
                {(!isAuthenticated ||
                  addressMode === 'form' ||
                  (isAuthenticated && !addressesLoading && savedAddresses.length === 0)) && (
                  <div className="space-y-4">
                    {/* Back to saved list link */}
                    {isAuthenticated && savedAddresses.length > 0 && addressMode === 'form' && (
                      <button
                        type="button"
                        onClick={handleBackToAddressList}
                        className="text-sm text-secondary hover:underline font-sans"
                      >
                        ← Back to saved addresses
                      </button>
                    )}

                    {/* Full Name */}
                    <div>
                      <label className="block text-bodyText font-semibold mb-2 font-sans text-sm">
                        {co.fullName || 'Full Name'}
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border-2 bg-surface text-bodyText font-sans focus:outline-none focus:border-secondary transition-colors ${
                          errors.fullName ? 'border-red-500' : 'border-gray-200'
                        }`}
                        placeholder="Enter your full name"
                      />
                      {errors.fullName && (
                        <p className="text-red-500 text-xs mt-1 font-sans">{errors.fullName}</p>
                      )}
                    </div>

                    {/* Phone + Email */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-bodyText font-semibold mb-2 font-sans text-sm">
                          {co.phone || 'Phone Number'}
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-xl border-2 bg-surface text-bodyText font-sans focus:outline-none focus:border-secondary transition-colors ${
                            errors.phone ? 'border-red-500' : 'border-gray-200'
                          }`}
                          placeholder="10-digit phone number"
                        />
                        {errors.phone && (
                          <p className="text-red-500 text-xs mt-1 font-sans">{errors.phone}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-bodyText font-semibold mb-2 font-sans text-sm">
                          {co.email || 'Email'}
                        </label>
                        {isAuthenticated ? (
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            disabled
                            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-100 text-bodyText font-sans cursor-not-allowed opacity-70"
                          />
                        ) : (
                          <>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-xl border-2 bg-surface text-bodyText font-sans focus:outline-none focus:border-secondary transition-colors ${
                                errors.email ? 'border-red-500' : 'border-gray-200'
                              }`}
                              placeholder="your@email.com"
                            />
                            {errors.email && (
                              <p className="text-red-500 text-xs mt-1 font-sans">{errors.email}</p>
                            )}
                            <p className="text-xs text-textSecondary mt-1 font-sans">
                              Order confirmation will be sent here
                            </p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Street Address */}
                    <div>
                      <label className="block text-bodyText font-semibold mb-2 font-sans text-sm">
                        {co.addressLine || 'Address'}
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 rounded-xl border-2 bg-surface text-bodyText font-sans focus:outline-none focus:border-secondary transition-colors ${
                          errors.address ? 'border-red-500' : 'border-gray-200'
                        }`}
                        placeholder="Street address, building, flat number"
                      />
                      {errors.address && (
                        <p className="text-red-500 text-xs mt-1 font-sans">{errors.address}</p>
                      )}
                    </div>

                    {/* City / State / Pincode */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-bodyText font-semibold mb-2 font-sans text-sm">
                          {co.city || 'City'}
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-xl border-2 bg-surface text-bodyText font-sans focus:outline-none focus:border-secondary transition-colors ${
                            errors.city ? 'border-red-500' : 'border-gray-200'
                          }`}
                          placeholder="City"
                        />
                        {errors.city && (
                          <p className="text-red-500 text-xs mt-1 font-sans">{errors.city}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-bodyText font-semibold mb-2 font-sans text-sm">
                          {co.state || 'State'}
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-xl border-2 bg-surface text-bodyText font-sans focus:outline-none focus:border-secondary transition-colors ${
                            errors.state ? 'border-red-500' : 'border-gray-200'
                          }`}
                          placeholder="State"
                        />
                        {errors.state && (
                          <p className="text-red-500 text-xs mt-1 font-sans">{errors.state}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-bodyText font-semibold mb-2 font-sans text-sm">
                          {co.pincode || 'Pincode'}
                        </label>
                        <input
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          className={`w-full px-4 py-3 rounded-xl border-2 bg-surface text-bodyText font-sans focus:outline-none focus:border-secondary transition-colors ${
                            errors.pincode ? 'border-red-500' : 'border-gray-200'
                          }`}
                          placeholder="6-digit pincode"
                          maxLength="6"
                        />
                        {errors.pincode && (
                          <p className="text-red-500 text-xs mt-1 font-sans">{errors.pincode}</p>
                        )}
                      </div>
                    </div>

                    {/* Save as default */}
                    {isAuthenticated && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          name="saveAsDefault"
                          checked={formData.saveAsDefault}
                          onChange={handleInputChange}
                          className="w-5 h-5 rounded accent-secondary"
                        />
                        <span className="text-bodyText font-sans text-sm">
                          {co.saveAsDefault || 'Save as default address'}
                        </span>
                      </label>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ════════════════════════════════════════════
                STEP 2 — SHIPPING OPTIONS
            ════════════════════════════════════════════ */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-heading text-headingText">
                  {co.deliveryMethod || 'Delivery Method'}
                </h2>

                {/* Pincode missing */}
                {(!formData.pincode || !/^\d{6}$/.test(formData.pincode)) && (
                  <div className="p-4 bg-surface rounded-xl border border-gray-200 text-sm text-textSecondary font-sans">
                    {co.pincodeMissing || 'Enter a valid pincode to load shipping rates.'}
                  </div>
                )}

                {/* Loading */}
                {formData.pincode && /^\d{6}$/.test(formData.pincode) && shippingLoading && (
                  <div className="flex items-center gap-3 p-4 bg-surface rounded-xl">
                    <svg className="animate-spin h-5 w-5 text-secondary" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    <span className="text-sm text-bodyText font-sans">
                      {co.fetchingRates || 'Fetching shipping rates…'}
                    </span>
                  </div>
                )}

                {/* No coverage (422) — block checkout */}
                {formData.pincode && /^\d{6}$/.test(formData.pincode) && !shippingLoading && shippingError === 'no_coverage' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-sm font-semibold text-red-700 font-sans">
                      🚫 {co.noCoverage || 'Delivery is not available to this pincode.'}
                    </p>
                    <p className="text-xs text-red-500 mt-1 font-sans">
                      {co.noCoverageDesc || 'Please check the pincode or contact us.'}
                    </p>
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="mt-3 text-sm text-secondary hover:underline font-sans"
                    >
                      ← {co.editAddress || 'Edit address'}
                    </button>
                  </div>
                )}

                {/* Service unavailable (503) — show fallback + retry */}
                {formData.pincode && /^\d{6}$/.test(formData.pincode) && !shippingLoading && shippingError === 'unavailable' && (
                  <div className="flex items-start justify-between gap-3 p-3 bg-yellow-50 rounded-xl border border-yellow-200 text-xs text-yellow-700 font-sans">
                    <div className="flex items-start gap-2">
                      <span>⚠️</span>
                      <span>{co.serviceUnavailable || 'Delivery options are unavailable right now. Standard options shown.'}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        lastFetchedPincodeRef.current = '';
                        fetchShippingOptions(formData.pincode);
                      }}
                      className="shrink-0 underline font-semibold hover:no-underline"
                    >
                      {co.retryRates || 'Retry'}
                    </button>
                  </div>
                )}

                {/* Shipping options list */}
                {formData.pincode && /^\d{6}$/.test(formData.pincode) && !shippingLoading && shippingError !== 'no_coverage' && (
                  <div className="space-y-3">
                    {/* Fallback notice */}
                    {isFallback && shippingError === 'fallback' && (
                      <div className="flex items-center gap-2 p-3 bg-surface rounded-xl border border-gray-200 text-xs text-textSecondary font-sans">
                        <span>ℹ️</span>
                        <span>{co.shippingFallback || 'Live rates unavailable — standard options shown.'}</span>
                      </div>
                    )}
                    {shippingError === 'rate_limit' && (
                      <div className="flex items-center gap-2 p-3 bg-yellow-50 rounded-xl border border-yellow-200 text-xs text-yellow-700 font-sans">
                        <span>⚠️</span>
                        <span>Rate limited — please wait a moment. Standard options shown.</span>
                      </div>
                    )}

                    {displayOptions.map((option) => (
                      <label
                        key={option.id}
                        className={`flex items-center gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                          selectedShippingOption?.id === option.id
                            ? 'border-secondary bg-secondary/5'
                            : 'border-gray-200 hover:border-secondary/50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="shippingOption"
                          value={option.id}
                          checked={selectedShippingOption?.id === option.id}
                          onChange={() => setSelectedShippingOption(option)}
                          className="w-5 h-5 accent-secondary shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-semibold text-bodyText font-sans text-sm">
                              {option.name}
                            </span>
                            {option.isRecommended && (
                              <span className="text-xs bg-secondary/10 text-secondary px-2 py-0.5 rounded-full font-sans">
                                ✓ {co.recommended || 'Recommended'}
                              </span>
                            )}
                            {option.isAir && (
                              <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-sans">
                                ✈ Air
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-xs text-textSecondary font-sans">
                              {option.etd || `${option.etdDays} ${co.deliveryDays || 'days'}`}
                            </span>
                            {option.rating != null && (
                              <span className="text-xs text-textSecondary font-sans">
                                ★ {option.rating}
                              </span>
                            )}
                            {option.codAvailable && (
                              <span className="text-xs text-green-600 font-sans">COD</span>
                            )}
                          </div>
                        </div>
                        <span className="font-bold text-secondary font-sans shrink-0">
                          {option.price === 0 ? 'Free' : `₹${option.price}`}
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Delivery summary */}
                <div className="mt-6 p-4 bg-surface rounded-xl">
                  <h3 className="font-semibold text-bodyText font-sans text-sm mb-2">
                    {co.deliveryTo || 'Delivery to:'}
                  </h3>
                  <p className="text-sm text-textSecondary font-sans">{formData.fullName}</p>
                  <p className="text-sm text-textSecondary font-sans">
                    {formData.address}, {formData.city}
                  </p>
                  <p className="text-sm text-textSecondary font-sans">
                    {formData.state} — {formData.pincode}
                  </p>
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════
                STEP 3 — REVIEW
            ════════════════════════════════════════════ */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <h2 className="text-2xl font-heading text-headingText">
                  {co.reviewOrder || 'Review Order'}
                </h2>

                <div className="space-y-2">
                  <h3 className="font-semibold text-bodyText font-sans text-sm mb-3">Items</h3>
                  {cart.map((item) => (
                    <div
                      key={item.id}
                      className="flex justify-between py-2 border-b border-gray-200 last:border-0"
                    >
                      <span className="text-bodyText font-sans text-sm">
                        {item.name} × {item.quantity}
                      </span>
                      <span className="font-semibold text-bodyText font-sans text-sm">
                        ₹{item.price * item.quantity}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="p-4 bg-surface rounded-xl">
                  <h3 className="font-semibold text-bodyText font-sans text-sm mb-3">
                    Delivery Address
                  </h3>
                  <p className="text-sm text-textSecondary font-sans">{formData.fullName}</p>
                  <p className="text-sm text-textSecondary font-sans">{formData.phone}</p>
                  <p className="text-sm text-textSecondary font-sans">{formData.address}</p>
                  <p className="text-sm text-textSecondary font-sans">
                    {formData.city}, {formData.state} — {formData.pincode}
                  </p>
                </div>

                {selectedShippingOption && (
                  <div className="p-4 bg-surface rounded-xl">
                    <h3 className="font-semibold text-bodyText font-sans text-sm mb-2">
                      Shipping Method
                    </h3>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-textSecondary font-sans">
                          {selectedShippingOption.name}
                        </p>
                        <p className="text-xs text-textSecondary font-sans mt-0.5">
                          {selectedShippingOption.etd || `${selectedShippingOption.etdDays} ${co.deliveryDays || 'days'}`}
                        </p>
                      </div>
                      <span className="text-sm font-semibold text-secondary font-sans">
                        {selectedShippingOption.price === 0 ? 'Free' : `₹${selectedShippingOption.price}`}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Navigation Buttons ── */}
            <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="px-6 py-3 rounded-xl border-2 border-secondary text-secondary font-semibold font-sans hover:bg-secondary hover:text-primaryBg transition-all"
                >
                  {co.back || 'Back'}
                </button>
              )}

              {currentStep < 3 && (
                <button
                  type="button"
                  onClick={handleNextStep}
                  disabled={
                    (currentStep === 2 && shippingError === 'no_coverage') ||
                    (currentStep === 2 && shippingLoading)
                  }
                  className="ml-auto px-6 py-3 rounded-xl bg-secondary text-primaryBg font-semibold font-sans hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {currentStep === 2 && shippingLoading
                    ? co.ratesUpdating || 'Updating rates…'
                    : co.continue || 'Continue'}
                </button>
              )}

              {currentStep === 3 && (
                <button
                  type="button"
                  onClick={handlePayment}
                  className="ml-auto px-6 py-3 rounded-xl bg-secondary text-primaryBg font-semibold font-sans hover:opacity-90 transition-all"
                >
                  {co.payNow || 'Pay'} ₹{total.toLocaleString('en-IN')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── Order Summary Sidebar ── */}
        <div className="lg:col-span-1">
          <div className="sticky top-32 bg-cardBg rounded-2xl p-6 shadow-card">
            <h3 className="text-xl font-heading text-headingText mb-4">Order Summary</h3>

            <div className="space-y-3 mb-6 pb-6 border-b border-gray-200">
              {cart.map((item) => (
                <div
                  key={item.id}
                  className="flex justify-between text-sm text-bodyText font-sans"
                >
                  <span className="truncate pr-2">{item.name} × {item.quantity}</span>
                  <span className="font-semibold shrink-0">₹{item.price * item.quantity}</span>
                </div>
              ))}
            </div>

            <div className="space-y-3 text-sm text-bodyText font-sans">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span>₹{subtotal.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between">
                <span>Shipping</span>
                <span>
                  {selectedShippingOption
                    ? selectedShippingOption.price === 0
                      ? 'Free'
                      : `₹${selectedShippingOption.price}`
                    : '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Tax (18% GST)</span>
                <span>₹{tax.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-base font-bold text-secondary pt-3 border-t border-gray-200">
                <span>Total</span>
                <span>₹{total.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <p className="text-xs text-textSecondary mt-4 font-sans">
              {selectedShippingOption
                ? 'Final price including shipping and taxes.'
                : 'Shipping cost will be calculated in step 2.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
